import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Input,
  InputGroup,
  Button,
  Badge,
} from "reactstrap";
import { Link } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import ModalStatus from "./ModalStatus";
import AwesomePagination from "../../../Components/Common/AwesomePagination";
import { toast } from "react-toastify";
import { APIClient } from "../../../helpers/api_helper";
import FeatherIcon from "feather-icons-react";
import moment from "moment";
import br from "../../../assets/images/flags/br.svg";

// AIDEV-NOTE: This component displays operations with new data structure (single moeda, direction-based)
const apiClient = new APIClient();

const TabelaOperacoes = () => {
  const [loading, setLoading] = useState(false);
  const [operacoes, setOperacoes] = useState([]);
  const [shouldFetch, setShouldFetch] = useState(true);

  // Estados para busca e filtros
  const [termo, setTermo] = useState("");
  const [inputTermo, setInputTermo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [direcaoFilter, setDirecaoFilter] = useState("");

  // Estados para paginação
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);

  // Estados para modal
  const [operacao, setOperacao] = useState(null);
  const [modalStatus, setModalStatus] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);

  // AIDEV-TODO: Update API call to match new data structure with direction-based operations
  const fetchOperacoes = useCallback(async () => {
    setLoading(true);
    const response = await apiClient.post("/operacao/busca", {
      termo: termo,
      status: statusFilter,
      direcao: direcaoFilter,
      pagina: pagina,
      itens_pagina: 20,
      order_by: "created_at",
      order: "desc",
    });

    setLoading(false);

    if (response.sucesso) {
      setOperacoes(response.dados.data);
      setTotalPaginas(response.dados.last_page);
      setTotalItens(response.dados.total);
    } else {
      toast.error(response.mensagem);
    }
  }, [termo, statusFilter, direcaoFilter, pagina]);

  useEffect(() => {
    if (shouldFetch) {
      const fetchData = async () => {
        await fetchOperacoes();
        setShouldFetch(false);
      };
      fetchData();
    }
  }, [shouldFetch, fetchOperacoes]);

  document.title = "Operações | Painel Fort Exchange";

  function formatName(name) {
    if (!name) return "-";
    let lowerCaseName = name.toLowerCase();
    let properName = lowerCaseName.replace(
      /\b\w/g,
      function (letter, index, fullText) {
        const previousLetter = fullText[index - 1];
        const isWordAfterApostrophe = previousLetter === "'";
        if (isWordAfterApostrophe) {
          return letter;
        }
        const word = fullText.slice(index).match(/^\w+/)[0];
        const lowerCaseWords = ["de", "da", "do", "dos", "das"];
        if (lowerCaseWords.includes(word)) {
          return letter;
        }
        return letter.toUpperCase();
      }
    );

    properName = properName.replace(/'\w/g, function (letter) {
      return letter.toUpperCase();
    });

    return properName;
  }

  // AIDEV-NOTE: Currency formatting with proper BRL display
  const formatCurrency = (value) => {
    if (!value) {
      return "R$ 0,00";
    }
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // AIDEV-NOTE: Crypto formatting with 6 decimal places and currency suffix
  const formatCrypto = (value, moeda) => {
    if (!value || !moeda) {
      return "0,000000";
    }
    return `${parseFloat(value).toLocaleString('pt-BR', {
      minimumFractionDigits: 6,
      maximumFractionDigits: 6
    })} ${moeda}`;
  };

  const handleTermoKeyDown = (e) => {
    if (e.key === "Enter") {
      setTermo(inputTermo);
      setPagina(1);
      setShouldFetch(true);
    }
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === "status") {
      setStatusFilter(value);
    } else if (filterType === "direcao") {
      setDirecaoFilter(value);
    }
    setPagina(1);
    setShouldFetch(true);
  };

  const clearFilters = () => {
    setTermo("");
    setInputTermo("");
    setStatusFilter("");
    setDirecaoFilter("");
    setPagina(1);
    setShouldFetch(true);
  };

  return (
    <React.Fragment>
      <ModalStatus
        operacao={operacao}
        setOperacao={setOperacao}
        setmodalStatus={setModalStatus}
        modalStatus={modalStatus}
        setShouldFetch={setShouldFetch}
        formatCurrency={formatCurrency}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Operações" pageTitle={"Aplicações"} />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center border-0">
                  <h5 className="card-title mb-0 flex-grow-1">
                    Operações OTC
                  </h5>
                  <div className="flex-shrink-0">
                    <div className="flax-shrink-0 hstack gap-2">
                      <Link to="/nova-operacao" className="btn btn-primary">
                        Nova Operação
                      </Link>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  {/* Filtros */}
                  <Row className="mb-3">
                    <Col md={6}>
                      <InputGroup>
                        <div className="input-group-text">
                          <FeatherIcon icon="search" className="icon-dual" />
                        </div>
                        <Input
                          type="text"
                          placeholder="Buscar por cliente, moeda, direção..."
                          value={inputTermo}
                          onChange={(e) => setInputTermo(e.target.value)}
                          onKeyDown={handleTermoKeyDown}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={2}>
                      <Input
                        type="select"
                        value={statusFilter}
                        onChange={(e) =>
                          handleFilterChange("status", e.target.value)
                        }
                      >
                        <option value="">Todos os Status</option>
                        <option value="aguardando">Aguardando</option>
                        <option value="completada">Completada</option>
                        <option value="estornada">Estornada</option>
                        <option value="cancelada">Cancelada</option>
                      </Input>
                    </Col>
                    <Col md={2}>
                      <Input
                        type="select"
                        value={direcaoFilter}
                        onChange={(e) =>
                          handleFilterChange("direcao", e.target.value)
                        }
                      >
                        <option value="">Todas as Direções</option>
                        <option value="compra">Compra</option>
                        <option value="venda">Venda</option>
                      </Input>
                    </Col>
                    <Col md={2}>
                      <Button
                        color="info"
                        onClick={clearFilters}
                        className="btn-sm"
                        disabled={!termo && !statusFilter && !direcaoFilter}
                      >
                        <FeatherIcon icon="x" className="me-1" />
                        Limpar Filtros
                      </Button>
                    </Col>
                  </Row>

                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "400px",
                      }}
                    >
                      <Spinner height={100} width={100} />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Cliente</th>
                              <th>Direção</th>
                              <th>Moeda</th>
                              <th>Quantidade</th>
                              <th>Cotação</th>
                              <th>Total BRL</th>
                              <th>Lucro Fort</th>
                              <th>Status</th>
                              <th>Data</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {operacoes.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={11}
                                  className="text-center text-muted py-4"
                                >
                                  Nenhuma operação encontrada.
                                </td>
                              </tr>
                            ) : (
                              operacoes.map((operacao) => (
                                <tr key={operacao.id}>
                                  {/* ID */}
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <Link
                                        to="#"
                                        className="currency_name flex-grow-1 ms-2 text-body"
                                      >
                                        {operacao.id}
                                      </Link>
                                    </div>
                                  </td>

                                  {/* Cliente */}
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <Link
                                        to="#"
                                        className="currency_name flex-grow-1 ms-2 text-body"
                                      >
                                        <b className="text-info">
                                          {operacao.user
                                            ? formatName(operacao.user.name)
                                            : "-"}
                                        </b>
                                      </Link>
                                    </div>
                                  </td>

                                  {/* Direção */}
                                  <td>
                                    <Badge
                                      color={
                                        operacao.direcao === "compra"
                                          ? "success"
                                          : "info"
                                      }
                                    >
                                      <FeatherIcon
                                        icon={
                                          operacao.direcao === "compra"
                                            ? "trending-up"
                                            : "trending-down"
                                        }
                                        className="me-1"
                                        size={12}
                                      />
                                      {operacao.direcao === "compra" 
                                        ? "Compra" 
                                        : "Venda"}
                                    </Badge>
                                  </td>

                                  {/* Moeda */}
                                  <td>
                                    <div className="d-flex align-items-center">
                                      {operacao.moeda === "BRL" ? (
                                        <img
                                          src={br}
                                          alt=""
                                          className="rounded-circle avatar-xxs me-2"
                                        />
                                      ) : (
                                        <img
                                          src={`https://api.fort.exchange/crypto-icons/${(operacao.moeda ===
                                          "POL"
                                            ? "MATIC"
                                            : operacao.moeda
                                          ).toLowerCase()}.svg`}
                                          alt=""
                                          className="rounded-circle avatar-xxs me-2"
                                        />
                                      )}
                                      {operacao.moeda}
                                    </div>
                                  </td>

                                  {/* Quantidade */}
                                  <td>
                                    {operacao.moeda === "BRL"
                                      ? formatCurrency(operacao.quantidade)
                                      : formatCrypto(operacao.quantidade, operacao.moeda)}
                                  </td>

                                  {/* Cotação */}
                                  <td>
                                    {formatCurrency(operacao.cotacao)}
                                  </td>

                                  {/* Total BRL */}
                                  <td>
                                    {formatCurrency(operacao.total_brl)}
                                  </td>

                                  {/* Lucro Fort */}
                                  <td>
                                    {formatCurrency(operacao.taxa_fort)}
                                  </td>

                                  {/* Status */}
                                  <td>
                                    <Badge
                                      color={
                                        operacao.status === "cancelada"
                                          ? "danger"
                                          : operacao.status === "aguardando"
                                          ? "warning"
                                          : operacao.status === "completada"
                                          ? "success"
                                          : "primary"
                                      }
                                    >
                                      {operacao.status.charAt(0).toUpperCase() +
                                        operacao.status.slice(1)}
                                    </Badge>
                                  </td>

                                  {/* Data */}
                                  <td>
                                    <b>
                                      {moment(operacao.created_at).format(
                                        "DD/MM/YYYY"
                                      )}
                                    </b>
                                    <br />
                                    <small className="text-muted">
                                      {moment(operacao.created_at).format(
                                        "HH:mm"
                                      )}
                                    </small>
                                  </td>

                                  {/* Ações */}
                                  <td>
                                    <Button
                                      color="success"
                                      className="btn btn-sm"
                                      onClick={() => {
                                        setOperacao(operacao);
                                        setModalStatus(true);
                                      }}
                                      disabled={loadingButton}
                                    >
                                      Ver Operação
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      {totalPaginas > 1 && (
                        <AwesomePagination
                          pagina={pagina}
                          totalPaginas={totalPaginas}
                          setPagina={(p) => {
                            setPagina(p);
                            setShouldFetch(true);
                          }}
                        />
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TabelaOperacoes;
